<template>
  <div>
    <ListingLayout
      :show-data-table="false"
      show-date
      date-selected="yesterday"
      :product-options="['oem', 'cso']"
      :filter-names="['locations', 'charge_point']"
      @filterOption="onFiltersChange"
    >
      <!-- <template #addFilters> </template> -->

      <!--  -->
      <div class="sla-dashboard">
        <!-- ═══════════════ HEADER ═══════════════ -->
        <v-card
          flat
          class="header-card mb-4"
          dark
        >
          <v-card-text class="d-flex align-center flex-wrap py-4">
            <div>
              <div class="d-flex align-center">
                <v-icon
                  large
                  class="mr-3"
                >
                  mdi-file-chart
                </v-icon>
                <div>
                  <div class="text-h5 font-weight-bold">
                    SLA Dashboard
                  </div>
                  <div class="caption white--text text--lighten-2">
                    {{ dashboard?.title }}
                  </div>
                </div>
              </div>
            </div>
            <v-spacer />
            <div class="text-right mr-3">
              <div class="caption white--text text--lighten-2">
                Reporting Period
              </div>
              <div class="font-weight-medium">
                {{ periodLabel }}
              </div>
            </div>
            <refresh-button
              :is-loading="isLoading"
              @reload="fetchData"
            />
          </v-card-text>
          <v-progress-linear
            v-if="isLoading"
            indeterminate
            color="teal accent-3"
            height="2"
          />
        </v-card>

        <!-- ═══════════════ KPI SUMMARY CARDS ═══════════════ -->
        <v-row
          dense
          class="mb-2"
        >
          <v-col
            v-for="kpi in kpiCards"
            :key="kpi.label"
            cols="12"
            sm="6"
            md="3"
          >
            <v-card
              outlined
              class="kpi-card"
              :style="{ borderTop: '4px solid ' + kpi.color }"
            >
              <v-card-text class="py-3">
                <div class="d-flex align-center justify-space-between">
                  <div>
                    <div class="overline grey--text text--darken-1">
                      <v-tooltip
                        v-if="kpi?.tooltip"
                        top
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-bind="attrs"
                            v-on="on"
                          >
                            <span>{{ kpi.label }}</span>
                            <span
                              class="mdi ml-2 mdi-information-variant-circle-outline"
                            />
                          </div>
                        </template>
                        <span>
                          {{ kpi.tooltip }}
                        </span>
                      </v-tooltip>
                      <span v-else>{{ kpi.label }}</span>
                    </div>
                    <div
                      class="text-h4 font-weight-bold"
                      :style="{ color: kpi.color }"
                    >
                      {{ kpi.value }}
                    </div>
                    <div class="caption grey--text">
                      {{ kpi.sub }}
                    </div>
                  </div>
                  <v-avatar
                    :color="kpi.color"
                    size="44"
                    class="lighten-avatar"
                  >
                    <v-icon dark>
                      {{ kpi.icon }}
                    </v-icon>
                  </v-avatar>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- ═══════════════ LEGEND ═══════════════ -->
        <v-card
          outlined
          class="mb-4"
        >
          <v-card-text class="py-2 d-flex align-center flex-wrap caption">
            <span
              class="mr-4"
            ><v-icon
              small
              color="success"
            >mdi-circle</v-icon>
              Green = On Target</span>
            <span
              class="mr-4"
            ><v-icon
              small
              color="warning"
            >mdi-circle</v-icon>
              Amber = At Risk</span>
            <span
              class="mr-4"
            ><v-icon
              small
              color="error"
            >mdi-circle</v-icon>
              Red = Breach</span>
            <v-divider
              vertical
              class="mx-3"
            />
            <span><v-icon
              small
              color="grey darken-3"
            >mdi-calculator</v-icon>
              Auto-calculated from telemetry</span>
          </v-card-text>
        </v-card>

        <!-- ═══════════════ SECTION TABS ═══════════════ -->
        <v-card outlined>
          <v-tabs
            v-model="activeTab"
            background-color="primary"
            dark
            slider-color="teal accent-3"
            show-arrows
          >
            <v-tab>
              <v-icon
                left
                small
              >
                mdi-clipboard-list-outline
              </v-icon>
              A. Obligation Register
            </v-tab>
            <v-tab>
              <v-icon
                left
                small
              >
                mdi-calendar-check
              </v-icon>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                  >
                    <span>B. Monthly Tracker</span>
                    <span
                      class="mdi ml-2 mdi-information-variant-circle-outline"
                    />
                  </div>
                </template>
                <span>
                  A month-by-month scorecard showing where targets were met or
                  missed (Red / Amber / Green).
                </span>
              </v-tooltip>
            </v-tab>
            <v-tab>
              <v-icon
                left
                small
              >
                mdi-function-variant
              </v-icon>

              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <div
                    v-bind="attrs"
                    v-on="on"
                  >
                    <span>C. α-Factor Availability</span>
                    <span
                      class="mdi ml-2 mdi-information-variant-circle-outline"
                    />
                  </div>
                </template>
                <span>
                  Working time after setting aside planned maintenance, compared
                  to the goal.
                </span>
              </v-tooltip>
            </v-tab>
            <v-tab>
              <v-icon
                left
                small
              >
                mdi-alert-octagon-outline
              </v-icon>
              D. Breach Log
              <v-chip
                v-if="openBreaches"
                x-small
                color="error"
                class="ml-2"
              >
                {{ openBreaches }}
              </v-chip>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- ─────────── A. SLA OBLIGATION REGISTER ─────────── -->
            <v-tab-item>
              <v-card-title class="section-title">
                SLA Obligation Register
                <v-spacer />
                <v-text-field
                  v-model="registerSearch"
                  append-icon="mdi-magnify"
                  label="Search SLAs"
                  single-line
                  hide-details
                  dense
                  outlined
                  style="max-width: 260px"
                />
              </v-card-title>
              <v-data-table
                :headers="registerHeaders"
                :items="register"
                :search="registerSearch"
                :loading="isLoading"
                disable-pagination
                hide-default-footer
                class="register-table"
              >
                <template #item.sla_id="{ item }">
                  <div class="d-flex align-center">
                    <strong class="primary--text">{{ item.sla_id }}</strong>
                    <v-tooltip
                      v-if="!item.computable"
                      top
                    >
                      <template #activator="{ on }">
                        <v-icon
                          small
                          class="ml-1"
                          color="grey"
                          v-on="on"
                        >
                          mdi-information-outline
                        </v-icon>
                      </template>
                      <span>{{
                        item.no_data_reason || "No data source available"
                      }}</span>
                    </v-tooltip>
                  </div>
                </template>
                <template #item.category="{ item }">
                  <v-chip
                    x-small
                    label
                    outlined
                    :color="categoryColor(item.category)"
                  >
                    {{ item.category }}
                  </v-chip>
                </template>
                <template #item.bands="{ item }">
                  <div class="d-flex flex-nowrap">
                    <v-chip
                      x-small
                      class="mr-1 white--text"
                      color="success"
                    >
                      {{ item.bands && item.bands.green }}
                    </v-chip>
                    <v-chip
                      x-small
                      class="mr-1"
                      color="warning"
                    >
                      {{ item.bands && item.bands.amber }}
                    </v-chip>
                    <v-chip
                      x-small
                      class="white--text"
                      color="error"
                    >
                      {{ item.bands && item.bands.red }}
                    </v-chip>
                  </div>
                </template>
                <template #item.status="{ item }">
                  <v-chip
                    small
                    :color="item.status === 'Active' ? 'teal' : 'grey'"
                    dark
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-tab-item>

            <!-- ─────────── B. MONTHLY COMPLIANCE TRACKER ─────────── -->
            <v-tab-item>
              <v-card-title class="section-title">
                Monthly SLA Compliance Tracker
                <v-spacer />
                <span class="caption grey--text mr-2">{{ periodLabel }}</span>
              </v-card-title>
              <div
                v-if="!trackerRows.length"
                class="pa-6 text-center grey--text"
              >
                {{
                  isLoading
                    ? "Loading…"
                    : "Monthly tracker not available for this window."
                }}
              </div>
              <div
                v-else
                class="table-scroll"
              >
                <table class="tracker-table">
                  <thead>
                    <tr>
                      <th class="text-left sticky-col">
                        SLA / Obligation
                      </th>
                      <th class="text-center">
                        Target
                      </th>
                      <th
                        v-for="m in trackerMonths"
                        :key="m.month"
                        class="text-center month-col"
                      >
                        {{ m.month_name || m.month }}
                      </th>
                      <th class="text-center">
                        YTD Breaches
                      </th>
                      <th class="text-center">
                        YTD Compliance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in trackerRows"
                      :key="row.sla_id"
                    >
                      <td class="sticky-col">
                        <strong class="primary--text">{{ row.sla_id }}</strong>
                        <div class="caption grey--text">
                          {{ row.obligation }}
                        </div>
                      </td>
                      <td class="text-center font-weight-medium">
                        {{ row.target }}
                      </td>
                      <td
                        v-for="(m, mi) in trackerMonths"
                        :key="row.sla_id + m.month"
                        class="text-center month-col"
                      >
                        <div class="d-flex align-center justify-center">
                          <span class="calc-cell mr-1">{{
                            monthActual(row, mi)
                          }}</span>
                          <v-tooltip top>
                            <template #activator="{ on }">
                              <v-icon
                                small
                                class="ml-1"
                                :color="statusColor(monthStatus(row, mi))"
                                v-on="on"
                              >
                                mdi-circle
                              </v-icon>
                            </template>
                            <span>{{ statusLabel(monthStatus(row, mi)) }}</span>
                          </v-tooltip>
                        </div>
                      </td>
                      <td class="text-center">
                        <v-chip
                          small
                          :color="row.ytd_breaches ? 'error' : 'success'"
                          dark
                        >
                          {{ row.ytd_breaches || 0 }}
                        </v-chip>
                      </td>
                      <td
                        class="text-center font-weight-bold"
                        :class="ytdComplianceClass(row)"
                      >
                        {{
                          row.ytd_compliance_percent != null
                            ? row.ytd_compliance_percent.toFixed(0) + "%"
                            : "—"
                        }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <v-card-text class="caption grey--text">
                YTD Compliance = % of evaluated months that were green. Amber
                counts as "at risk" but not a breach.
              </v-card-text>
            </v-tab-item>

            <!-- ─────────── C. α-FACTOR AVAILABILITY CALCULATOR ─────────── -->
            <v-tab-item>
              <v-card-title class="section-title">
                α-Factor Adjusted Availability Calculator
              </v-card-title>
              <v-card-text>
                <v-alert
                  dense
                  text
                  color="teal"
                  border="left"
                  class="mb-4"
                >
                  <strong>Formula:</strong> Adjusted Availability % = [
                  (Available Hours + α × Excluded Hours) ÷ Total Hours ] × 100
                  <br>
                  <span class="caption">
                    α = weighting factor for excluded downtime (force majeure,
                    scheduled maintenance, PSO-side issues)
                  </span>
                </v-alert>

                <v-row
                  align="center"
                  class="mb-4"
                >
                  <v-col
                    cols="6"
                    md="3"
                  >
                    <div class="overline grey--text">
                      α Factor
                    </div>
                    <div class="text-h6 font-weight-medium">
                      {{ alpha.toFixed(2) }}
                    </div>
                  </v-col>
                  <v-col
                    cols="6"
                    md="3"
                  >
                    <div class="overline grey--text">
                      SLA Target
                    </div>
                    <div class="text-h6 font-weight-medium">
                      {{ slaTargetPct }}%
                    </div>
                  </v-col>
                </v-row>

                <v-data-table
                  :headers="alphaHeaders"
                  :items="stations"
                  :loading="isLoading"
                  disable-pagination
                  hide-default-footer
                  class="alpha-table"
                >
                  <template #item.station_id="{ item }">
                    <strong class="primary--text">{{ item.station_id }}</strong>
                  </template>
                  <template
                    v-for="field in [
                      'total_hours',
                      'total_downtime_hrs',
                      'scheduled_downtime_hrs',
                      'force_majeure_hrs',
                      'pso_side_downtime_hrs',
                      'total_excluded_hrs',
                      'unexcused_downtime_hrs',
                    ]"
                    #[`item.${field}`]="{ item }"
                  >
                    <span
                      :key="field"
                      class="calc-cell"
                    >{{ item[field] }}</span>
                  </template>
                  <template #item.raw_availability_percent="{ item }">
                    <span
                      class="calc-cell"
                    >{{ item.raw_availability_percent.toFixed(2) }}%</span>
                  </template>
                  <template #item.adjusted_availability_percent="{ item }">
                    <strong
                      :class="item.sla_met ? 'success--text' : 'error--text'"
                    >
                      {{ item.adjusted_availability_percent.toFixed(2) }}%
                    </strong>
                  </template>
                  <template #item.sla_met="{ item }">
                    <v-chip
                      small
                      dark
                      :color="item.sla_met ? 'success' : 'error'"
                    >
                      <v-icon
                        left
                        x-small
                      >
                        {{ item.sla_met ? "mdi-check" : "mdi-close" }}
                      </v-icon>
                      {{ item.sla_met ? "MET" : "BREACH" }}
                    </v-chip>
                  </template>
                </v-data-table>

                <!-- Fleet summary -->
                <v-row
                  dense
                  class="mt-4"
                >
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-card
                      outlined
                      class="text-center py-3"
                    >
                      <div class="overline grey--text">
                        Fleet Raw Availability
                      </div>
                      <div class="text-h5 font-weight-bold">
                        {{ fleetRaw.toFixed(2) }}%
                      </div>
                    </v-card>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-card
                      outlined
                      class="text-center py-3"
                    >
                      <div class="overline grey--text">
                        Fleet Adjusted Availability
                      </div>
                      <div
                        class="text-h5 font-weight-bold"
                        :class="
                          fleetAdjusted >= slaTargetPct
                            ? 'success--text'
                            : 'error--text'
                        "
                      >
                        {{ fleetAdjusted.toFixed(2) }}%
                      </div>
                    </v-card>
                  </v-col>
                  <v-col
                    cols="12"
                    md="4"
                  >
                    <v-card
                      outlined
                      class="text-center py-3"
                    >
                      <div class="overline grey--text">
                        Stations in Breach
                      </div>
                      <div class="text-h5 font-weight-bold error--text">
                        {{ stationsInBreach }}
                      </div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-tab-item>

            <!-- ─────────── D. BREACH & ESCALATION LOG ─────────── -->
            <v-tab-item>
              <v-card-title class="section-title">
                SLA Breach &amp; Escalation Log
              </v-card-title>
              <v-data-table
                :headers="breachHeaders"
                :items="breaches"
                :loading="isLoading"
                disable-pagination
                hide-default-footer
                no-data-text="No breaches logged — the network is fully compliant 🎉"
              >
                <template #item.breach_id="{ item }">
                  <strong class="error--text">{{ item.breach_id }}</strong>
                </template>
                <template #item.severity="{ item }">
                  <v-chip
                    x-small
                    dark
                    :color="severityColor(item.severity)"
                  >
                    {{ item.severity }}
                  </v-chip>
                </template>
                <template #item.rca_submitted="{ item }">
                  <v-icon
                    small
                    :color="item.rca_submitted ? 'success' : 'grey'"
                  >
                    {{
                      item.rca_submitted
                        ? "mdi-check-circle"
                        : "mdi-minus-circle-outline"
                    }}
                  </v-icon>
                </template>
                <template #item.status="{ item }">
                  <v-chip
                    x-small
                    dark
                    :color="
                      item.status === 'Closed'
                        ? 'success'
                        : item.status === 'In Progress'
                          ? 'warning'
                          : 'error'
                    "
                  >
                    {{ item.status }}
                  </v-chip>
                </template>
              </v-data-table>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </div>

      <!--  -->

      <!-- <template #actionModals>
        
      </template> -->
      <!-- Action Dialog -->
      <!-- <v-dialog v-model="dialog" max-width="800px">
        <start-transtions :customer-data="selectedRow"/>
      </v-dialog> -->
    </ListingLayout>
  </div>
</template>

<script>
import { ListingLayoutMixins } from "@/mixins/ListingLayoutMixins";

import { mapGetters } from "vuex";
// import { debounce } from "lodash";
export default {
  components: {
    ListingLayout: () => import("@/layouts/section/ListingLayout.vue"),
    refreshButton: () => import("@/components/base/RefreshButton.vue"),
  },
  mixins: [ListingLayoutMixins],

  data() {
    return {
      filters: {},
      subFilters: {},
      //
      activeTab: 0,
      registerSearch: "",

      // ── Table headers ──
      registerHeaders: [
        { text: "Charge Point ID", value: "sla_id", width: 90 },
        { text: "Obligation", value: "obligation" },
        { text: "Category", value: "category" },
        { text: "Metric", value: "metric" },
        { text: "Target", value: "target" },
        { text: "Measurement", value: "measurement_method" },
        { text: "Frequency", value: "reporting_frequency" },
        { text: "Grace", value: "grace_period" },
        {
          text: "Thresholds (G / A / R)",
          value: "bands",
          sortable: false,
          width: 220,
        },
        { text: "Review", value: "review_frequency" },
        { text: "Status", value: "status" },
      ],
      alphaHeaders: [
        { text: "Station ID", value: "station_id", width: 130 },
        { text: "Total Hrs", value: "total_hours", width: 100 },
        { text: "Downtime (hrs)", value: "total_downtime_hrs", width: 110 },
        {
          text: "Scheduled (hrs)",
          value: "scheduled_downtime_hrs",
          width: 110,
        },
        { text: "Force Majeure (hrs)", value: "force_majeure_hrs", width: 120 },
        { text: "PSO-Side (hrs)", value: "pso_side_downtime_hrs", width: 110 },
        { text: "Excluded", value: "total_excluded_hrs", width: 90 },
        { text: "Unexcused", value: "unexcused_downtime_hrs", width: 95 },
        { text: "Raw %", value: "raw_availability_percent", width: 90 },
        {
          text: "Adjusted %",
          value: "adjusted_availability_percent",
          width: 100,
        },
        { text: "SLA Met?", value: "sla_met", width: 110 },
      ],
      breachHeaders: [
        { text: "Breach ID", value: "breach_id", width: 120 },
        { text: "SLA", value: "sla_id", width: 90 },
        { text: "Station", value: "station" },
        { text: "Date", value: "date" },
        { text: "Description", value: "description" },
        { text: "Severity", value: "severity" },
        { text: "Root Cause", value: "root_cause" },
        { text: "Duration", value: "duration_of_breach" },
        { text: "RCA", value: "rca_submitted", align: "center" },
        { text: "Status", value: "status" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      authUser: "getUser",
      isLoading: "slaDashboard/getLoading",
      meta: "slaDashboard/getMeta",
      dashboardRaw: "slaDashboard/getList",
    }),

    dashboard() {
      return this.dashboardRaw || {};
    },
    windowInfo() {
      return this.dashboard.window || {};
    },
    summary() {
      return this.dashboard.summary || {};
    },
    register() {
      return this.dashboard.sla_obligation_register || [];
    },
    trackerSection() {
      return this.dashboard.monthly_compliance_tracker || null;
    },
    trackerMonths() {
      return (this.trackerSection && this.trackerSection.months) || [];
    },
    trackerRows() {
      return (this.trackerSection && this.trackerSection.rows) || [];
    },
    alphaSection() {
      return this.dashboard.a_factor_availability || null;
    },
    stations() {
      return (this.alphaSection && this.alphaSection.stations) || [];
    },
    network() {
      return (this.alphaSection && this.alphaSection.network) || {};
    },
    breaches() {
      return this.dashboard.breach_escalation_log || [];
    },
    alpha() {
      return this.alphaSection && this.alphaSection.a_factor != null
        ? this.alphaSection.a_factor
        : 1;
    },
    slaTargetPct() {
      return this.alphaSection && this.alphaSection.sla_target != null
        ? this.alphaSection.sla_target
        : 98;
    },
    periodLabel() {
      if (this.dashboard.reporting_period)
        return this.dashboard.reporting_period;
      if (this.windowInfo.start && this.windowInfo.end) {
        return `${this.windowInfo.start.slice(
          0,
          10
        )} → ${this.windowInfo.end.slice(0, 10)}`;
      }
      return "—";
    },
    openBreaches() {
      return this.breaches.filter((b) => b.status !== "Closed").length;
    },
    atRiskCount() {
      // SLA rows whose latest reported month is amber
      return this.trackerRows.filter((row) => {
        const months = row.monthly || [];
        for (let i = months.length - 1; i >= 0; i--) {
          if (months[i] && months[i].status !== "no_data") {
            return months[i].status === "amber";
          }
        }
        return false;
      }).length;
    },
    kpiCards() {
      const availability = this.summary.availability || {};
      const sla = availability.sla || {};
      return [
        {
          label: "Total Ticket SLA",
          value: 5,
          sub: "Number of ticket created",
          icon: "mdi-shield-check",
          color: "#1B5E20",
        },
        {
          label: "Network Availability",
          value:
            availability.network_availability_percent != null
              ? availability.network_availability_percent.toFixed(1) + "%"
              : "—",
          sub:
            sla.target_percent != null
              ? (sla.meets_target ? "Meeting " : "Below ") +
                sla.target_percent +
                "% target"
              : "Window availability",
          icon: "mdi-shield-check",
          color: sla.meets_target === false ? "#C62828" : "#1B5E20",
          tooltip:
            "How much of the time your charging network was working and ready to use.",
        },
        {
          label: "Number of Active SLAs",
          value: this.register.filter((s) => s.status === "Active").length,
          sub: "Under agreement",
          icon: "mdi-clipboard-list",
          color: "#00897B",
        },
        {
          label: "YTD Breaches",
          value: this.breaches.length,
          sub: "Logged in this window",
          icon: "mdi-alert-octagon",
          color: "#C62828",
        },
        {
          label: "At Risk (Amber)",
          value: this.atRiskCount,
          sub: "Latest reported month",
          icon: "mdi-alert",
          color: "#F9A825",
        },
      ];
    },
    fleetRaw() {
      return this.network.raw_availability_percent || 0;
    },
    fleetAdjusted() {
      return this.network.adjusted_availability_percent || 0;
    },
    stationsInBreach() {
      return this.stations.filter((s) => s.sla_met === false).length;
    },

    filterNames() {
      return [""];
    },
  },
  watch: {
    // "options.tenant_id": {
    //   handler: function (val) {},
    // },
    options() {
      this.fetchData();
    },
  },
  async mounted() {
    if (!this.authUser.isAdmin) {
      const params = { tenant_id: this.authUser.tenant_id };
      await this.$store.dispatch("slaDashboard/list", params);
    }
  },
  beforeDestroy() {
    const paramsset = { ...this.options };
    const params = {
      name: "sla_dashboard",
      filters: paramsset,
    };
    this.$store.commit("redirect/SET_FILTERS", params);
  },
  methods: {
    async fetchData() {
      const params = { ...this.options };
      await this.$store.dispatch("slaDashboard/list", params).then(() => {
        if (this.options.page > (this.meta?.lastPage || 1)) {
          this.options.page = 1;
        }
      });
    },
    statusColor(status) {
      return (
        {
          green: "success",
          amber: "warning",
          red: "error",
          no_data: "grey lighten-1",
        }[status] || "grey lighten-1"
      );
    },
    statusLabel(status) {
      return (
        {
          green: "On Target",
          amber: "At Risk",
          red: "Breach",
          no_data: "Not reported",
        }[status] || "Not reported"
      );
    },
    monthActual(row, monthIndex) {
      const entry = (row.monthly || [])[monthIndex];
      return entry && entry.actual != null ? entry.actual : "—";
    },
    monthStatus(row, monthIndex) {
      const entry = (row.monthly || [])[monthIndex];
      return entry ? entry.status : "no_data";
    },
    ytdComplianceClass(row) {
      const b = row.ytd_breaches || 0;
      return b === 0
        ? "success--text"
        : b === 1
        ? "warning--text"
        : "error--text";
    },
    categoryColor(cat) {
      return (
        {
          Availability: "green darken-2",
          Reliability: "blue darken-1",
          Quality: "purple",
          Compliance: "brown",
          Integration: "indigo",
          Maintenance: "orange darken-2",
          Technical: "teal darken-1",
        }[cat] || "grey"
      );
    },
    severityColor(sev) {
      return (
        {
          P1: "error",
          High: "error",
          P2: "orange darken-2",
          Medium: "orange darken-2",
          P3: "blue-grey",
          Low: "blue-grey",
        }[sev] || "grey"
      );
    },
  },
};
</script>
<style scoped>
.sla-dashboard {
  background: #f4f6f5;
  min-height: 100vh;
}

/* Header — ORKO deep forest green */
.header-card {
  background: linear-gradient(
    135deg,
    #0e2f25 0%,
    #1b4332 60%,
    #14532d 100%
  ) !important;
  border-radius: 10px;
}

.kpi-card {
  border-radius: 8px;
  background: #fff;
}
.lighten-avatar {
  opacity: 0.92;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1b4332;
  border-bottom: 1px solid #e0e0e0;
}

/* Monthly tracker table */
.table-scroll {
  overflow-x: auto;
}
.tracker-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 1100px;
}
.tracker-table th {
  background: #1b4332;
  color: #fff;
  padding: 10px 8px;
  font-weight: 600;
  white-space: nowrap;
}
.tracker-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #eceff1;
}
.tracker-table tbody tr:hover {
  background: #f1f8f5;
}
.sticky-col {
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 1;
  min-width: 220px;
}
.tracker-table th.sticky-col {
  background: #1b4332;
  z-index: 2;
}
.month-col {
  min-width: 110px;
}

/* α table */
.calc-cell {
  font-family: "Roboto Mono", monospace;
  font-size: 13px;
  color: #263238;
}

.register-table >>> th {
  background: #f1f8f5 !important;
  white-space: nowrap;
}
.alpha-table >>> th {
  background: #f1f8f5 !important;
  white-space: nowrap;
}
</style>
